// BlockEditorPane — the shared shell every per-block editor renders inside.
// WL-011 (Fable Hail Mary Wave 2).
//
// Enforces the SIDE-PANE LAW (DESIGN-DECISIONS-LOG row 29): ALL block/feature
// editing lives in the Edit-Wheel side pane's sub-section — never inside the
// Segment. The Segment shows finalized display only. This component is that
// sub-section frame: a header (back chevron + block glyph + "<Segment> Segment"
// crumb) over a scrollable body that holds the editor's Basic + Advanced groups.
//
// Async-state contracts (EW-012): pass `status` = "loading" | "empty" | "error"
// | "ready" and the matching contract slot renders instead of the body. Provide
// onRetry for the error state, and empty/loading/error nodes (or defaults from
// ./contracts) via the *Slot props.
//
// Accessibility (WL-027): header back-control is a real >=44px button with an
// aria-label; the scroll body is a labelled region; focus ring visible.

import React from "react";
import styled from "styled-components";
import { colors } from "../../../utils";
import { fontFamily, brand, focusRing } from "../../../theme/tokens";
import { EditorLoading, EditorEmpty, EditorError } from "./contracts";
import { fieldChrome } from "./EditorField";

const INK = fieldChrome.ink;

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: ${colors.white};
  font-family: ${fontFamily};
  box-sizing: border-box;
  & * { box-sizing: border-box; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #f7f2fa;
  flex: none;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-left: -8px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: ${INK};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover { background: #f6f7f9; }
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing.shadow};
  }
  svg { width: 20px; height: 20px; }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Glyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f3ecff;
  color: ${brand.primaryActive};
  flex: none;
  svg { width: 18px; height: 18px; }
`;

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Crumb = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${fieldChrome.muted};
  line-height: 14px;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px 16px;
`;

const BackChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BlockEditorPane = ({
  blockName,
  segmentName,
  glyph,
  onBack,
  status = "ready",
  onRetry,
  loadingSlot,
  emptySlot,
  errorSlot,
  errorMessage,
  children,
  ...rest
}) => {
  let body;
  if (status === "loading") body = loadingSlot || <EditorLoading />;
  else if (status === "empty") body = emptySlot || <EditorEmpty blockName={blockName} />;
  else if (status === "error")
    body = errorSlot || <EditorError message={errorMessage} onRetry={onRetry} />;
  else body = children;

  return (
    <Shell role="region" aria-label={`${blockName} settings`} {...rest}>
      <Header>
        {onBack && (
          <BackBtn type="button" aria-label="Back to blocks" onClick={onBack}>
            <BackChevron />
          </BackBtn>
        )}
        {glyph && <Glyph aria-hidden="true">{glyph}</Glyph>}
        <Titles>
          <Title>{blockName}</Title>
          {segmentName && <Crumb>{segmentName} Segment</Crumb>}
        </Titles>
      </Header>
      <Body>{body}</Body>
    </Shell>
  );
};

export default BlockEditorPane;
